import {
  connectorDirectory,
  listedConnectorDirectory,
} from "../lib/connectorDirectory";
import {
  type DropdownSection,
  DropdownSections,
  RowButton,
  SearchableDropdown,
  SubtleButton,
} from "./SearchableDropdown";
import { type AppInfo, ConnectorOption, ConnectorRow } from "./ConnectorRow";
import { EmptyState } from "./EmptyState";
import { Tooltip } from "./Tooltip";
import { useMakeUrl, routes } from "../lib/urls";

export type ConnectedItem = AppInfo & {
  manageTo: string;
  onManage?: () => void;
  onRemove?: () => void;
  section?: string;
};

export type AvailableItem = AppInfo & {
  onSelect: () => void;
};

export type DisconnectedItem = {
  id: string;
  appType: string;
  slug?: string;
  options: AppInfo[];
  onSelect: (connectedAppId: string) => void;
  onCreateNew?: () => void;
};

type Props = {
  opener: React.ReactNode;
  parentNoun: string;
  connected: ConnectedItem[];
  available: AvailableItem[];
  availableLabel: string;
  disconnected?: DisconnectedItem[];
  onCreateApp?: (identifier: string) => void;
  margin?: number;
  side?: React.ComponentProps<typeof SearchableDropdown>["side"];
  style?: React.CSSProperties;
  disabled?: boolean;
  "aria-label"?: string;
};

export function ConnectorSelectorDropdown({
  opener,
  parentNoun,
  connected,
  available,
  availableLabel,
  disconnected = [],
  onCreateApp,
  margin,
  side,
  style,
  disabled,
  "aria-label": ariaLabel,
}: Props) {
  const makeUrl = useMakeUrl();
  return (
    <SearchableDropdown
      opener={opener}
      margin={margin}
      side={side}
      style={style}
      disabled={disabled}
      aria-label={ariaLabel}
      searchPlaceholder="Search connectors…"
      height={360}
    >
      {(query, close, shiftHeld) => {
        const lowerQuery = query.toLowerCase().trim();
        const match = (name: string) => name.toLowerCase().includes(lowerQuery);
        const filteredConnected = lowerQuery
          ? connected.filter((a) => match(a.name))
          : connected;
        const filteredAvailable = lowerQuery
          ? available.filter((a) => match(a.name))
          : available;
        const customApp = connectorDirectory.find(
          (a) => a.identifier === "apiKey"
        );
        const directoryResults = lowerQuery
          ? listedConnectorDirectory.filter((a) => match(a.name))
          : [];
        const createItems = !onCreateApp
          ? []
          : directoryResults.length
            ? directoryResults
            : filteredConnected.length === 0 &&
                filteredAvailable.length === 0 &&
                customApp
              ? [customApp]
              : [];

        const sections: DropdownSection[] = [];

        const connectedSections = new Set(
          filteredConnected.map((app) => app.section ?? `On this ${parentNoun}`)
        );
        for (const label of connectedSections) {
          sections.push({
            label,
            rows: filteredConnected
              .filter(
                (app) => (app.section ?? `On this ${parentNoun}`) === label
              )
              .map((app) => (
                <ConnectorOption
                  key={app.id}
                  app={app}
                  to={app.manageTo}
                  close={() => {
                    app.onManage?.();
                    close();
                  }}
                  hover={
                    app.onRemove && (
                      <Tooltip content={`Remove from ${parentNoun}`}>
                        <SubtleButton
                          icon="circle-minus"
                          variant="secondary"
                          onClick={app.onRemove}
                        />
                      </Tooltip>
                    )
                  }
                />
              )),
          });
        }

        if (disconnected.length) {
          sections.push({
            label: "Needs connection",
            rows: disconnected.map((item) => {
              const appDef = connectorDirectory.find(
                (a) => a.identifier === item.appType
              );
              const name = item.slug ?? appDef?.name ?? item.appType;
              return (
                <RowButton
                  key={item.id}
                  disabled={!item.onCreateNew}
                  onClick={() => {
                    item.onCreateNew?.();
                    close();
                  }}
                >
                  <ConnectorRow
                    template={{ name, identifier: item.appType }}
                    name={name}
                    secondary="Disconnected"
                    grayscale
                  />
                </RowButton>
              );
            }),
          });
        }

        if (filteredAvailable.length) {
          sections.push({
            label: availableLabel,
            rows: filteredAvailable.map((app) => (
              <ConnectorOption
                key={app.id}
                app={app}
                onClick={app.onSelect}
                openTo={makeUrl(routes.modal.connector, { id: app.id })}
                shiftHeld={shiftHeld}
                close={close}
              />
            )),
          });
        }

        if (createItems.length) {
          sections.push({
            label: "Add new",
            rows: createItems.map((app) => (
              <RowButton
                key={app.identifier}
                onClick={() => {
                  onCreateApp?.(app.identifier);
                  close();
                }}
              >
                <ConnectorRow
                  template={{ name: app.name, identifier: app.identifier }}
                  name={app.name}
                  secondary={app.tagLine}
                />
              </RowButton>
            )),
          });
        }

        return (
          <DropdownSections
            sections={sections}
            empty={
              <EmptyState
                iconName={lowerQuery ? "text-search" : "api-key-custom"}
                iconSize={24}
                title={
                  lowerQuery ? "No connectors found" : "No connectors available"
                }
              />
            }
          />
        );
      }}
    </SearchableDropdown>
  );
}
